import { getRemoteCredentials } from '@shared/domain/credentialDefinitions';
import type { Credential, FormState, TrustScore } from '@shared/domain/types';
import { getApplicationStepPath } from '@shared/app/applicationFlow';

export const mockCredentials: Credential[] = [
  // Brønnøysundregistrene (Eksistens)
  {
    id: '1',
    source: 'brreg',
    type: 'stiftet',
    title: 'Stiftet > 3 år siden',
    description: 'Etablert 2021',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-01-15'),
  },
  {
    id: '2',
    source: 'brreg',
    type: 'arsregnskap',
    title: 'Årsregnskap levert',
    description: 'Innen frist siste 3 år',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-03-01'),
  },
  {
    id: '3',
    source: 'brreg',
    type: 'kvinneandel',
    title: 'Kvinneandel > 40%',
    description: 'Styresammensetning',
    points: 5,
    verified: true,
    issuedAt: new Date('2024-01-20'),
  },
  // Skatteetaten (Betalingsvilje)
  {
    id: '4',
    source: 'skatteetaten',
    type: 'mva',
    title: 'Ingen utestående MVA',
    description: 'Siste terminforfall ok',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-02-01'),
  },
  {
    id: '5',
    source: 'skatteetaten',
    type: 'utlegg',
    title: 'Ingen utleggsforretninger',
    description: 'Tvangsinnkreving (0)',
    points: 15,
    verified: true,
    issuedAt: new Date('2024-02-15'),
  },
  {
    id: '6',
    source: 'skatteetaten',
    type: 'arbeidsgiveravgift',
    title: 'Arbeidsgiveravgift',
    description: 'Rapportert månedlig',
    points: 5,
    verified: true,
    issuedAt: new Date('2024-03-10'),
  },
  // Bank & Finans (Likviditet)
  {
    id: '7',
    source: 'bank',
    type: 'bedriftskonto',
    title: 'Aktiv bedriftskonto',
    description: '> 2 år historikk',
    points: 5,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
  {
    id: '8',
    source: 'bank',
    type: 'overtrekk',
    title: 'Ingen overtrekk',
    description: 'Siste 6 mnd',
    points: 5,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
  {
    id: '9',
    source: 'bank',
    type: 'fakturaer',
    title: 'Fakturaer betalt i tide',
    description: '> 90% siste 12 mnd',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
  // Tilsyn & Sanksjoner (Atferd)
  {
    id: '10',
    source: 'tilsyn',
    type: 'dommer',
    title: 'Piloted with the City of Oslo',
    description: 'We piloted together with the City of Oslo. Municipality approved 👍',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
  {
    id: '11',
    source: 'tilsyn',
    type: 'sanksjoner',
    title: 'Third-party circularity assessment',
    description: 'Independent assessor confirms high circularity performance',
    points: 10,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
  {
    id: '12',
    source: 'tilsyn',
    type: 'konkurskarantene',
    title: 'Company carbon footprint disclosure',
    description: 'Verified baseline and documented reduction plan',
    points: 5,
    verified: true,
    issuedAt: new Date('2024-03-15'),
  },
];

const enCredentialCopy: Record<string, { title: string; description: string }> = {
  stiftet: {
    title: 'Founded more than 3 years ago',
    description: 'Established in 2021',
  },
  arsregnskap: {
    title: 'Annual accounts submitted',
    description: 'Submitted on time for the last 3 years',
  },
  kvinneandel: {
    title: 'Women representation above 40%',
    description: 'Board composition',
  },
  mva: {
    title: 'No outstanding VAT',
    description: 'Latest VAT due date cleared',
  },
  utlegg: {
    title: 'No enforcement proceedings',
    description: 'Debt enforcement (0)',
  },
  arbeidsgiveravgift: {
    title: 'Employer contribution',
    description: 'Reported monthly',
  },
  bedriftskonto: {
    title: 'Active business account',
    description: 'More than 2 years of account history',
  },
  overtrekk: {
    title: 'No overdrafts',
    description: 'Last 6 months',
  },
  fakturaer: {
    title: 'Invoices paid on time',
    description: 'More than 90% over the last 12 months',
  },
  dommer: {
    title: 'Piloted with the City of Oslo',
    description: 'We piloted together with the City of Oslo. Municipality approved',
  },
  sanksjoner: {
    title: 'Third-party circularity assessment',
    description: 'Independent assessor confirms high circularity performance',
  },
  konkurskarantene: {
    title: 'Company carbon footprint disclosure',
    description: 'Verified baseline and documented reduction plan',
  },
};

let cachedEnglishCredentials: Credential[] | null = null;

const innovasjonNorgeCredentialCopy: Record<string, { title: string; description: string }> = {
  dommer: {
    title: 'Gjennomført pilot',
    description: 'Smart avfallshåndtering ble pilotert og godkjent av Oslo kommune.',
  },
  sanksjoner: {
    title: 'Tredjepartsvurdering av sirkularitet',
    description: 'En uavhengig vurdering bekrefter høy sirkulær ytelse.',
  },
  konkurskarantene: {
    title: 'Dokumentert klimafotavtrykk',
    description: 'Verifisert utslippsgrunnlag og dokumentert reduksjonsplan.',
  },
};

function localizeCredentialToEnglish(credential: Credential): Credential {
  const copy = enCredentialCopy[credential.type];
  if (!copy) {
    return credential;
  }

  return {
    ...credential,
    title: copy.title,
    description: copy.description,
  };
}

export function getMockCredentialsForLocale(locale: string): Credential[] {
  if (!locale.toLowerCase().startsWith('en')) {
    return mockCredentials;
  }

  if (!cachedEnglishCredentials) {
    cachedEnglishCredentials = mockCredentials.map(localizeCredentialToEnglish);
  }

  return cachedEnglishCredentials;
}

export function getMockCredentialsForVariant(locale: string, variant: string): Credential[] {
  const credentials = getMockCredentialsForLocale(locale);
  if (variant === 'nordic-smart-city-v2') {
    return credentials.map(localizeCredentialToEnglish);
  }
  if (variant !== 'innovasjon-norge') return credentials;

  return credentials.map((credential) => {
    const copy = innovasjonNorgeCredentialCopy[credential.type];
    return copy ? { ...credential, ...copy } : credential;
  });
}

export const TRUST_SCORE_THRESHOLD = 33;

export function getStatus(earned: number): 'ok' | 'sjekk' | 'kritisk' {
  if (earned >= TRUST_SCORE_THRESHOLD) return 'ok';
  if (earned >= 25) return 'sjekk';
  return 'kritisk';
}

function computeScore(
  activeCredentials: Credential[],
  allCredentials: Credential[]
): TrustScore {
  const earned = activeCredentials.reduce((sum, c) => sum + c.points, 0);
  const maxPossible = allCredentials.reduce((sum, c) => sum + c.points, 0);
  return {
    earned,
    maxPossible,
    threshold: TRUST_SCORE_THRESHOLD,
    isApproved: earned >= TRUST_SCORE_THRESHOLD,
  };
}

export function calculateTrustScore(
  sentCredentialIds: string[],
  simulateRevocation: boolean = false
): TrustScore {
  const matching = [...mockCredentials, ...getRemoteCredentials('nb')].filter((c) =>
    sentCredentialIds.includes(c.id)
  );
  // A demo and a remote representation of the same scoring category count once.
  const sentCredentials = Array.from(new Map(matching.map((credential) => [credential.type, credential])).values());

  const activeCredentials = sentCredentials.filter((c) => {
    if (simulateRevocation && c.type === 'arsregnskap') return false;
    return c.verified;
  });

  return computeScore(activeCredentials, mockCredentials);
}

export function groupCredentialsBySource(
  credentials: Credential[]
): Record<string, Credential[]> {
  return credentials.reduce((acc, credential) => {
    if (!acc[credential.source]) acc[credential.source] = [];
    acc[credential.source].push(credential);
    return acc;
  }, {} as Record<string, Credential[]>);
}

export function getPartnerDestination(state: FormState): string {
  const hasStartedProofFlow =
    state.step2.requestedProofKeys.length > 0 ||
    state.step2.sentCredentials.length > 0;
  return hasStartedProofFlow
    ? getApplicationStepPath(3) ?? '/soknad/samarbeidspartner'
    : getApplicationStepPath(2) ?? '/soknad/stotte';
}

export function calculateTrustScoreWithRevocations(
  credentials: Credential[],
  revokedIds: Set<string>
): TrustScore {
  // Bidirectional toggle logic:
  // - If originally valid (verified=true): revoked means invalid, not revoked means valid
  // - If originally invalid (verified=false): revoked means restored/valid, not revoked means invalid
  const activeCredentials = credentials.filter((c) => {
    const isRevoked = revokedIds.has(c.id);
    return c.verified ? !isRevoked : isRevoked;
  });

  return computeScore(activeCredentials, credentials);
}
